import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import styles from './Page.module.css';
import s from './Estoque.module.css';
import { fmtQtd } from '../utils/formatQtd';

const fmtBRL = v => Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const saldo = es =>
  (parseFloat(es.quantidade) || 0) - (parseFloat(es.reserva) || 0) + (parseFloat(es.futuro) || 0);

export default function Estoque() {
  const qc = useQueryClient();
  const [busca, setBusca] = useState('');
  const [modalEntrada, setModalEntrada] = useState(false);
  const [form, setForm] = useState({ produto_id: '', produto_nome: '', quantidade: '', custo_unitario: '', setor_id: '', numero_nota: '', fornecedor: '' });
  const [buscaProd, setBuscaProd] = useState('');
  const [sugestoesProd, setSugestoesProd] = useState([]);

  const { data, isLoading } = useQuery({
    queryKey: ['estoque-por-setor'],
    queryFn: () => api.get('/estoque/por-setor').then(r => r.data),
  });

  const { data: alertas = [] } = useQuery({
    queryKey: ['alertas-estoque'],
    queryFn: () => api.get('/estoque/alertas').then(r => r.data),
  });

  const registrarEntrada = useMutation({
    mutationFn: d => api.post('/estoque/entrada', d),
    onSuccess: () => {
      qc.invalidateQueries(['estoque-por-setor']);
      qc.invalidateQueries(['alertas-estoque']);
      qc.invalidateQueries(['produtos']);
      fecharModal();
    },
  });

  const setores  = data?.setores  || [];
  const produtos = (data?.produtos || []).filter(p => {
    if (!busca.trim()) return true;
    const b = busca.toLowerCase();
    return p.produto.toLowerCase().includes(b)
      || String(p.codigo_interno).includes(b)
      || (p.categoria || '').toLowerCase().includes(b);
  });

  function fecharModal() {
    setModalEntrada(false);
    setForm({ produto_id: '', produto_nome: '', quantidade: '', custo_unitario: '', setor_id: '', numero_nota: '', fornecedor: '' });
    setBuscaProd('');
    setSugestoesProd([]);
  }

  async function buscarProduto(termo) {
    setBuscaProd(termo);
    if (termo.length < 2) { setSugestoesProd([]); return; }
    try {
      const { data } = await api.get('/produtos', { params: { busca: termo, limite: 8 } });
      setSugestoesProd(Array.isArray(data) ? data : (data.produtos || []));
    } catch { setSugestoesProd([]); }
  }

  function selecionarProduto(p) {
    setForm(f => ({ ...f, produto_id: p.id, produto_nome: p.nome }));
    setBuscaProd(p.nome);
    setSugestoesProd([]);
  }

  function confirmarEntrada(e) {
    e.preventDefault();
    if (!form.produto_id) return;
    registrarEntrada.mutate({
      produto_id:     parseInt(form.produto_id),
      quantidade:     parseFloat(form.quantidade),
      custo_unitario: parseFloat(form.custo_unitario || 0),
      setor_id:       form.setor_id ? parseInt(form.setor_id) : undefined,
      numero_nota:    form.numero_nota || undefined,
      fornecedor:     form.fornecedor || undefined,
    });
  }

  /* número de colunas de dados por setor */
  const COLS_SETOR = 4; // Estq | Res | Fut | Saldo

  return (
    <div>
      <div className={styles.topbar}>
        <h2 className={styles.titulo}>Estoque por Local</h2>
        <div className={styles.acoes}>
          <input className={styles.busca} placeholder="Buscar produto..." value={busca}
            onChange={e => setBusca(e.target.value)} />
          <button className={styles.btnPrimario} onClick={() => setModalEntrada(true)}>+ Entrada</button>
        </div>
      </div>

      {alertas.length > 0 && (
        <div className={s.alertaBar}>
          <strong>⚠ {alertas.length} produto(s) com estoque abaixo do mínimo:</strong>
          <span className={s.alertaLista}>
            {alertas.map(a => `${a.produto} (atual: ${fmtQtd(a.quantidade, a.unidade_fraciona)} / mín: ${fmtQtd(a.estoque_minimo, a.unidade_fraciona)})`).join(' · ')}
          </span>
        </div>
      )}

      {isLoading ? <p style={{ padding: 24 }}>Carregando...</p> : (
        <div className={s.tableWrap}>
          <table className={s.table}>
            <thead>
              {/* Linha 1: grupos */}
              <tr>
                <th rowSpan={2} className={s.thFix} style={{ width: 60 }}>Cód.</th>
                <th rowSpan={2} className={s.thFix}>Produto</th>
                <th rowSpan={2} className={s.thFix} style={{ width: 80 }}>Categoria</th>
                <th rowSpan={2} className={s.thFix} style={{ width: 40, textAlign: 'center' }}>UN</th>
                {setores.map(se => (
                  <th key={se.id} colSpan={COLS_SETOR} className={s.thGrupoSetor}>
                    {se.sigla} — {se.nome}
                  </th>
                ))}
                <th colSpan={COLS_SETOR} className={s.thGrupoTotal}>TOTAL</th>
                <th rowSpan={2} className={s.thFix} style={{ width: 70, textAlign: 'right' }}>Mín.</th>
                <th rowSpan={2} className={s.thFix} style={{ width: 70, textAlign: 'center' }}>Status</th>
              </tr>
              {/* Linha 2: sub-colunas */}
              <tr>
                {setores.map(se => (
                  <React.Fragment key={se.id}>
                    <th className={s.thSub}>Estq</th>
                    <th className={s.thSub}>Res</th>
                    <th className={s.thSub}>Fut</th>
                    <th className={s.thSub}>Saldo</th>
                  </React.Fragment>
                ))}
                <th className={s.thSub}>Estq</th>
                <th className={s.thSub}>Res</th>
                <th className={s.thSub}>Fut</th>
                <th className={s.thSub}>Saldo</th>
              </tr>
            </thead>
            <tbody>
              {produtos.length === 0 && (
                <tr><td colSpan={4 + (setores.length + 1) * COLS_SETOR + 2} className={s.vazio}>
                  Nenhum produto encontrado.
                </td></tr>
              )}
              {produtos.map(p => {
                const setoresprod = p.setores || [];
                const totQtd = setoresprod.reduce((acc, es) => acc + (parseFloat(es.quantidade) || 0), 0);
                const totRes = setoresprod.reduce((acc, es) => acc + (parseFloat(es.reserva)    || 0), 0);
                const totFut = setoresprod.reduce((acc, es) => acc + (parseFloat(es.futuro)     || 0), 0);
                const totSaldo = totQtd - totRes + totFut;
                const minimo   = parseFloat(p.estoque_minimo || 0);
                const abaixo   = minimo > 0 && totSaldo < minimo;
                const f3 = v => fmtQtd(v, p.unidade_fraciona);

                return (
                  <tr key={p.produto_id} className={abaixo ? s.rowBaixo : ''}>
                    <td className={s.tdCod}>{p.codigo_interno}</td>
                    <td className={s.tdProduto}><strong>{p.produto}</strong></td>
                    <td className={s.tdCat}>{p.categoria || '—'}</td>
                    <td className={s.tdUn}>{p.unidade || '—'}</td>

                    {setoresprod.map(es => {
                      const sd = saldo(es);
                      return (
                        <React.Fragment key={es.setor_id}>
                          <td className={s.tdNum}>{parseFloat(es.quantidade) !== 0 ? f3(es.quantidade) : <span className={s.zero}>—</span>}</td>
                          <td className={s.tdNum}>{parseFloat(es.reserva)    !== 0 ? f3(es.reserva)    : <span className={s.zero}>—</span>}</td>
                          <td className={s.tdNum}>{parseFloat(es.futuro)     !== 0 ? f3(es.futuro)     : <span className={s.zero}>—</span>}</td>
                          <td className={sd < 0 ? s.tdSaldoNeg : s.tdSaldoPos}>{f3(sd)}</td>
                        </React.Fragment>
                      );
                    })}

                    {/* Totais */}
                    <td className={s.tdTotalNum}><strong>{f3(totQtd)}</strong></td>
                    <td className={s.tdTotalNum}>{totRes !== 0 ? f3(totRes) : <span className={s.zero}>—</span>}</td>
                    <td className={s.tdTotalNum}>{totFut !== 0 ? f3(totFut) : <span className={s.zero}>—</span>}</td>
                    <td className={totSaldo < 0 ? s.tdTotalNeg : s.tdTotalPos}><strong>{f3(totSaldo)}</strong></td>

                    <td className={s.tdMin}>{f3(p.estoque_minimo)}</td>
                    <td className={s.tdStatus}>
                      <span className={abaixo ? s.badgeBaixo : s.badgeOk}>
                        {abaixo ? 'Baixo' : 'OK'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {modalEntrada && (
        <div className={styles.overlay}>
          <div className={s.modal}>
            <div className={s.modalHeader}>
              <h3>Entrada de Estoque</h3>
              <button className={s.modalClose} onClick={fecharModal}>✕</button>
            </div>

            <form onSubmit={confirmarEntrada} className={s.modalForm}>
              <label className={s.label}>
                Produto *
                <div className={s.prodBuscaWrap}>
                  <input className={s.input}
                    value={buscaProd}
                    onChange={e => buscarProduto(e.target.value)}
                    placeholder="Digite para buscar..."
                    autoFocus
                  />
                  {sugestoesProd.length > 0 && (
                    <div className={s.prodSugestoes}>
                      {sugestoesProd.map(p => (
                        <div key={p.id} className={s.prodSugestaoItem}
                          onMouseDown={() => selecionarProduto(p)}>
                          <span className={s.prodCod}>{p.codigo_interno}</span>
                          <span className={s.prodNome}>{p.nome}</span>
                          <span className={s.prodEstoque}>Estoque: {fmtQtd(p.estoque_atual, p.unidade_fraciona)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </label>

              <div className={s.row2}>
                <label className={s.label}>
                  Local de Estoque
                  <select className={s.input} value={form.setor_id}
                    onChange={e => setForm(f => ({ ...f, setor_id: e.target.value }))}>
                    <option value="">— todos (geral) —</option>
                    {setores.map(se => (
                      <option key={se.id} value={se.id}>{se.nome}</option>
                    ))}
                  </select>
                </label>
                <label className={s.label}>
                  Quantidade *
                  <input className={s.input} type="number" min="0.001" step="0.001" required
                    value={form.quantidade}
                    onChange={e => setForm(f => ({ ...f, quantidade: e.target.value }))} />
                </label>
              </div>

              <div className={s.row2}>
                <label className={s.label}>
                  Custo Unitário R$
                  <input className={s.input} type="number" step="0.01" min="0"
                    value={form.custo_unitario}
                    onChange={e => setForm(f => ({ ...f, custo_unitario: e.target.value }))} />
                </label>
                <label className={s.label}>
                  Nº Nota
                  <input className={s.input} value={form.numero_nota}
                    onChange={e => setForm(f => ({ ...f, numero_nota: e.target.value }))} />
                </label>
              </div>

              <label className={s.label}>
                Fornecedor
                <input className={s.input} value={form.fornecedor}
                  onChange={e => setForm(f => ({ ...f, fornecedor: e.target.value }))} />
              </label>

              {registrarEntrada.isError && (
                <p className={s.erro}>{registrarEntrada.error?.response?.data?.erro || 'Erro ao registrar.'}</p>
              )}

              <div className={s.modalAcoes}>
                <button type="button" className={styles.btnSecundario} onClick={fecharModal}>Cancelar</button>
                <button type="submit" className={styles.btnPrimario}
                  disabled={!form.produto_id || registrarEntrada.isPending}>
                  {registrarEntrada.isPending ? 'Registrando…' : 'Confirmar Entrada'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
